import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { FeedPerson, FeedRunCard } from "@signalist/shared-types";

import { AssignStatus } from "@/enum/list.enum";
import { api } from "@/redux/api";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import { useUndoIgnoreFeedRunMutation } from "@/pages/Private/redux/feed/feed.api";
import { feedSelector } from "@/pages/Private/redux/feed/feed.slice";
import { useBulkChangeStatusMutation } from "@/pages/Private/redux/list/list.api";

/** How long the confirmation / error toast stays up. */
const TOAST_MS = 4000;

/** The lead-status transitions the run-card overflow menu offers, with their toast copy. */
const SUCCESS_TOAST_KEY: Partial<Record<AssignStatus, string>> = {
	[AssignStatus.SAVED_FOR_LATER]: "feed.runCard.menu.reviewLaterToast",
	[AssignStatus.DRAFT]: "feed.runCard.menu.backToNewToast",
	[AssignStatus.ARCHIVED]: "feed.runCard.menu.moveToIgnoredToast",
};

interface FeedLeadStatus {
	/** Change the card's selected lead to `status`, then refetch the feed + counts. */
	setStatus: (card: FeedRunCard, person: FeedPerson, status: AssignStatus) => Promise<void>;
	/** Bottom-center toast copy; null when hidden. */
	toast: { message: string; variant: "success" | "error" } | null;
}

/**
 * Lead-status transitions from the feed run-card overflow menu — Review later
 * (SAVED_FOR_LATER → "To be reviewed"), Move back to New (DRAFT) and Move to
 * Ignored (ARCHIVED). Uses the bulk status-change endpoint (the plain
 * `PATCH /list_person` DTO ignores `assignStatus`), then invalidates the Feed +
 * counts tags so the card re-buckets. Owns a short toast; the page renders it.
 *
 * Back-to-New on a run that was "ignored this time" (the card carries
 * `ignoredAt`) is special: that ignore stamped the run *and* archived all its
 * leads, and the Ignored tab keys on `run.ignoredAt` — so a per-lead status
 * change would leave the card stuck. Those go through the run-level un-ignore,
 * which clears `ignoredAt` and restores every snapshotted lead.
 */
export const useFeedLeadStatus = (): FeedLeadStatus => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const { selectedLists } = useAppSelector(feedSelector);
	const [bulkChangeStatus] = useBulkChangeStatusMutation();
	const [undoIgnoreFeedRun] = useUndoIgnoreFeedRunMutation();

	const [toast, setToast] = useState<FeedLeadStatus["toast"]>(null);
	const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!toast) {
			return;
		}

		toastTimerRef.current = setTimeout(() => setToast(null), TOAST_MS);

		return () => {
			if (toastTimerRef.current) {
				clearTimeout(toastTimerRef.current);
			}
		};
	}, [toast]);

	const setStatus = async (card: FeedRunCard, person: FeedPerson, status: AssignStatus) => {
		// Degraded card: no lead to move (the menu items are hidden, but guard anyway).
		if (!person.listPersonId) {
			return;
		}

		const successKey = SUCCESS_TOAST_KEY[status];
		// Returning a run-ignored card to New = un-ignore the run (restores every
		// snapshotted lead + clears `ignoredAt`); a per-lead status change can't
		// clear the run flag the Ignored tab keys on. A runless card (PREC-3584) has no
		// run and `ignoredAt` is always null, so this branch never applies to it.
		const isUnignoreRun = status === AssignStatus.DRAFT && card.runId !== null && !!card.ignoredAt;

		try {
			if (isUnignoreRun && card.runId !== null) {
				await undoIgnoreFeedRun({
					runId: card.runId,
					listIds: selectedLists.map((list) => Number(list.id)),
				}).unwrap();
			} else {
				await bulkChangeStatus({
					listId: card.listId,
					assign: [{ listPersonId: person.listPersonId }],
					assignStatus: status,
				}).unwrap();
			}

			dispatch(api.util.invalidateTags(["Feed", "FeedCounts"]));
			setToast({
				message: successKey ? t(successKey, { name: person.name ?? "" }) : "",
				variant: "success",
			});
		} catch {
			setToast({ message: t("feed.runCard.menu.statusError"), variant: "error" });
		}
	};

	return { setStatus, toast };
};
